import React, { useEffect, useState, useRef } from "react";
import { updateCampaign } from "../../../api/UpdateCampaign";
import SharedTooltip from "../../shared/Tooltip/SharedTooltip";
import editClasses from "./EditCampaign.module.css";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import Editor from "../CampaignModal/Editor/Editor";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Compressor from "compressorjs";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import PrimaryButton from "../../shared/Buttons/PrimaryButton";
import CampaignUpdateForm from "./CampaignUpdateForm";
import CampaignShareBox from "./CampaignShareBox";
import TextInput from "../../shared/Inputs/TextInput";
import DropDown from "../../shared/Inputs/Dropdown";

const maxSize = 10 * 1024 * 1024;

function SortableItem({ id, value, width, onDelete, itemIndex, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const widthClass = width > 900 ? "w-1/3" : width > 530 ? "w-5/12" : "w-2/3";
  return (
    <div className={`${widthClass} relative px-1`} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="relative rounded shadow overflow-hidden">
        <img
          className="w-full object-cover h-[110px]"
          src={value}
          alt="campaign image"
          style={{ cursor: disabled ? "default" : "grab" }}
        />
        {!disabled && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDelete(itemIndex); }}
            className="absolute top-1 right-1 bg-white/80 hover:bg-white/95 rounded-full p-1 leading-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function SortableList({ items, width, onDelete, disabled, onSortEnd }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      onSortEnd({ oldIndex, newIndex });
    }
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div className="max-[900px]:ml-20">
          <div className="flex flex-wrap gap-2 overflow-auto">
            {items.map((value, index) => (
              <SortableItem key={value} id={value} value={value} itemIndex={index} width={width} onDelete={onDelete} disabled={disabled} />
            ))}
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
}

const projectOptions = [
  { value: "planning", label: "Planning" },
  { value: "construction", label: "Under Construction" },
  { value: "completed", label: "Completed" },
];

const EditCapitalCampaignForm = ({
  campaignId,
  campaignData,
  onClose,
}) => {
  const [capitalFormValues, setCapitalFormValues] = useState(() => {
    const imageUrls =
      campaignData?.campaign_images?.map((image) => image.image_url) || [];
    return {
      nonprofit: campaignData?.nonprofit || "",
      campaign_title: campaignData?.campaign_title || "",
      description: campaignData?.description || "",
      donation_goal: campaignData?.donation_goal || 0,
      donation_box_title: campaignData?.donation_box_title || "",
      video_urls: campaignData?.video_urls || "",
      images: campaignData?.campaign_images || [],
      image_urls: imageUrls,
      campaign_type: "capital",
      land_area: campaignData?.land_area || "",
      land_location: campaignData?.land_location || "",
      project_status: campaignData?.project_status || "",
      estimated_completion_date: campaignData?.estimated_completion_date || "",
    };
  });

  const [capitalFormErrors, setCapitalFormErrors] = useState({
    nonprofit: "",
    campaign_title: "",
    description: "",
    donation_goal: "",
    donation_box_title: "",
    video_urls: "",
    images: "",
    land_area: "",
    land_location: "",
    project_status: "",
    estimated_completion_date: "",
  });
  const { width } = useWindowDimensions();
  const [totalImages, setTotalImages] = useState({
    images: 0,
  });
  let imageCount = 0;
  const [formEnabled, setFormEnabled] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  const handleImageChange = async (event) => {
    let files = Array.from(event.target.files);
    let error = "";
    let isTooLarge = false;

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        error = "File size is too large (Must be under 3 MB).";
        isTooLarge = true;
        return false;
      }
      return true;
    });

    if (isTooLarge) {
      setCapitalFormErrors((prevState) => ({
        ...prevState,
        images: error,
      }));
      return;
    }

    try {
      const compressedFiles = await Promise.all(
        validFiles.map(async (file) => {
          return new Promise((resolve) => {
            new Compressor(file, {
              quality: 0.8,
              success: (compressedResult) => {
                resolve(new File([compressedResult], file.name, { type: compressedResult.type }));
              },
              error: () => resolve(file),
            });
          });
        })
      );

      setCapitalFormErrors((prevState) => ({
        ...prevState,
        images: "",
      }));

      const newImages = compressedFiles.map((file) => ({
        file,
        url: URL.createObjectURL(file),
      }));

      // Update totalImages count
      setTotalImages((prevState) => ({
        ...prevState,
        images: prevState.images + compressedFiles.length,
      }));

      setCapitalFormValues((prevState) => {
        // Generate order for new images
        const startingIndex = prevState.images.filter(
          (img) => !img.file
        ).length; // Only count uploaded images
        const newCampaignImages = newImages.map((img, index) => ({
          file: img.file,
          image_url: img.url, // Include the URL in new images
          order: startingIndex + index,
        }));

        return {
          ...prevState,
          images: [...prevState.images, ...newCampaignImages],
          image_urls: [
            ...prevState.image_urls,
            ...newImages.map((img) => img.url),
          ],
        };
      });
    } catch (error) {
      console.error("Error during file compression:", error);
    }
  };

  // Testing for required inputs
  const validateForm = (formValues, setFormErrors) => {
    const errors = {};

    // For all forms
    if (!formValues.campaign_title.trim()) {
      errors.campaign_title = "Campaign title is required";
    }
    if (!formValues.donation_goal) {
      errors.donation_goal = "Donation goal is required";
    }
    if (!formValues.description) {
      errors.description = "Description is required";
    }


    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Returns true if no errors
  };

  const appendFormValues = (formValues) => {
    const formData = new FormData();

    // Fields that should not be sent to the backend (read-only or computed)
    const excludedFields = [
      'last_archived', 'archived', 'created_at', 'id', 'nonprofit_name',
      'nonprofit_address', 'donations_details', 'update_info', 'image_urls',
      'updates', 'images', 'campaign_images'
    ];

    // Append non-image fields
    Object.keys(formValues).forEach((key) => {
      if (key === "description") {
        // Convert HTML content to string before appending
        formData.append(key, JSON.stringify(formValues[key]));
      } else if (!excludedFields.includes(key)) {
        // Append other non-excluded fields
        formData.append(key, formValues[key]);
      }

      // Add milestones_reached to FormData as a JSON string
      if (campaignData.milestones_reached) {
        formData.append(
          "milestones_reached",
          JSON.stringify(campaignData.milestones_reached)
        );
      }
    });

    return formData;
  };

  const handleCapitalSubmit = () => {
    if (validateForm(capitalFormValues, setCapitalFormErrors)) {
      const formData = appendFormValues(capitalFormValues);
      return formData;
    }

    setIsLoading(false); // Stop loading on validation failure
    return null; // Explicitly return null if validation fails
  };

  const submitCampaignUpdate = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    setIsLoading(true);

    let formData;
    formData = handleCapitalSubmit();

    if (formData) {
      try {
        // sorts campaign_images based on image_urls since image_urls changes order when moved
        const campaignImages = [...capitalFormValues.images];
        const imageUrls = [...capitalFormValues.image_urls];
        // campaignImages gets sorted based on image_urls
        // this gets sent to the backend as "images"
        campaignImages.sort(function (a, b) {
          return (
            imageUrls.indexOf(a.image_url) - imageUrls.indexOf(b.image_url)
          );
        });
        campaignImages.forEach((image, index) => {
          if (image.file) {
            // if image hasn't been uploaded yet, it should contain a File
            formData.append("new_images", image.file);
            formData.append("new_image_orders", index);
          } else {
            // else, sending image ID and order to the backend to change it
            formData.append("existing_image_ids", image.id);
            formData.append("existing_image_orders", index);
          }
        });

        // Send deleted image IDs to the backend
        deletedImageIds.forEach(id => {
          formData.append("deleted_image_ids", id);
        });

        const response = await updateCampaign(campaignData.id, formData);

        if (response.success && response.data) {
          setSuccessMsg("Your campaign has been successfully updated!");
          setErrorMsg("");
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        const formattedError = errorMessage;
        setSuccessMsg("");
        setErrorMsg(formattedError);
      }
    }
  };

  /* Handles Campaign Edit Update */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Prevent negative donation goals
    if (name === "donation_goal" && parseFloat(value) < 0) {
      return;
    }

    const finalValue =
      name === "project_status"
        ? projectOptions.find((option) => option.value.toString() === value)
            ?.value || value
        : value;
    setCapitalFormValues((prevState) => ({
      ...prevState,
      [name]: finalValue,
    }));

    // Clear states on input
    setSuccessMsg("");
    setErrorMsg("");
    setCapitalFormErrors({
      nonprofit: "",
      campaign_title: "",
      description: "",
      donation_goal: "",
      donation_box_title: "",
      video_urls: "",
      images: "",
      land_area: "",
      land_location: "",
      project_status: "",
      estimated_completion_date: "",
    });
  };

  const handleEditorChange = (content, delta, source, editor) => {
    setCapitalFormValues((prevState) => ({
      ...prevState,
      description: content, // Set description as HTML content
    }));
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setCapitalFormValues((prevState) => ({
      ...prevState,
      image_urls: arrayMove(prevState.image_urls, oldIndex, newIndex),
    }));
  };

  const handleDeleteImage = (index) => {
    const imageUrl = capitalFormValues.image_urls[index];
    const imageToDelete = capitalFormValues.images.find(img => img.image_url === imageUrl);

    // Track ID if it's an existing image (has id)
    if (imageToDelete?.id) {
      setDeletedImageIds(prev => [...prev, imageToDelete.id]);
    }

    // Remove from state
    setCapitalFormValues(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index),
      images: prev.images.filter(img => img.image_url !== imageUrl)
    }));

    // Update image counts
    setTotalImages(prev => ({ ...prev, images: prev.images - 1 }));
  };

  // Initialize totalImages count from existing campaign images
  useEffect(() => {
    const imageCount = capitalFormValues.images?.length || 0;
    setTotalImages({ images: imageCount });
  }, []);

  return (
    <>
      {/* update form  */}
      <CampaignUpdateForm campaignId={campaignId} campaignData={campaignData} />

      {/* Edit Campaign  */}
      <div className={`flex flex-row justify-between gap-8`}>
        <form
          onSubmit={submitCampaignUpdate}
          className={`flex flex-col w-full`}
        >
          {/* Image Upload/Rearrange */}
          <div className="flex flex-col justify-center items-center gap-5 mb-4">
{totalImages.images > 0 && (
              <p className="font-normal text-lg italic">
                Click and drag to reorder
              </p>
            )}
            <div className="flex flex-row justify-center w-full items-center">
              <div className="flex flex-col justify-center items-center w-64 ">
                <p className="text-xl text-left font-normal">
                  Upload New Image
                </p>
                <input
                  type="file"
                  name="images"
                  multiple
                  onChange={handleImageChange}
                  className="!p-2.5 text-black"
                  disabled={!formEnabled}
                />
              </div>
            </div>
            <SortableList
              width={width}
              items={capitalFormValues.image_urls}
              onSortEnd={onSortEnd}
              onDelete={handleDeleteImage}
              disabled={!formEnabled}
              axis="xy"
              helperClass={editClasses.sortable_helper}
              shouldCancelStart={(e) => {
                // Prevent drag when clicking delete button or its children
                return e.target.closest('[data-no-drag="true"]') !== null;
              }}
            />
          </div>

          {/* Campaign Details */}
          <div className="flex flex-col">
            <TextInput
              label="Campaign Title"
              subLabel="(Required)"
              type="text"
              name="campaign_title"
              placeholder="Campaign Title"
              value={capitalFormValues.campaign_title}
              onChange={handleInputChange}
              errorMessage={capitalFormErrors.campaign_title}
              disabled={!formEnabled}
            />

            <div className="flex flex-col gap-4">
              <h2 className="text-black font-roboto font-normal text-lg text-left !w-full block">
                Description
              </h2>
              <Editor
                defaultValue={capitalFormValues.description}
                onTextChange={handleEditorChange}
                readOnly={!formEnabled}
              />
              <p className="text-error font-medium !text-sm ml-4 min-h-[20px] font-roboto text-left">
                {capitalFormErrors.description || "\u00A0"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TextInput
                label="Donation Goal"
                subLabel="(Required)"
                type="number"
                name="donation_goal"
                placeholder="$"
                value={capitalFormValues.donation_goal}
                onChange={handleInputChange}
                errorMessage={capitalFormErrors.donation_goal}
                disabled={!formEnabled}
              />

              <TextInput
                label="Donation Box Title"
                subLabel={
                  <>
                    {" "}
                    (Max. 30 Chars){"  "}
                    <SharedTooltip
                      id="my-tooltip"
                      toolClass="flex justify-center items-center"
                      content="The heading right above the donation box"
                    />
                  </>
                }
                sublabelClassName="inline-flex items-center !text-xs"
                type="text"
                name="donation_box_title"
                maxLength="30"
                placeholder="Donation Box Title (Max. 30 Chars)"
                value={capitalFormValues.donation_box_title}
                onChange={handleInputChange}
                errorMessage={capitalFormErrors.donation_box_title}
                disabled={!formEnabled}
              />
            </div>
            <TextInput
              label="Video URL"
              type="text"
              name="video_urls"
              placeholder="Video URLs (Separate with comma)"
              value={capitalFormValues.video_urls}
              onChange={handleInputChange}
              errorMessage={capitalFormErrors.video_urls}
              disabled={!formEnabled}
            />
          </div>

          {/* Campaign Details */}
          <div className="flex flex-col">
            <h2 className="text-2xl text-left max-[530px]:text-center">
              Project Details
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <DropDown
                label="Project status"
                placeholder="Select an option"
                options={projectOptions}
                name="project_status"
                value={capitalFormValues.project_status}
                onChange={handleInputChange}
                errorMessage={capitalFormErrors.project_status}
                disabled={!formEnabled}
              />
              <TextInput
                label="Land Location"
                type="text"
                name="land_location"
                value={capitalFormValues.land_location}
                onChange={handleInputChange}
                errorMessage={capitalFormErrors.land_location}
                disabled={!formEnabled}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <TextInput
                label="Land Area"
                type="text"
                name="land_area"
                value={capitalFormValues.land_area}
                onChange={handleInputChange}
                errorMessage={capitalFormErrors.land_area}
                disabled={!formEnabled}
              />
              <TextInput
                label="Estimated Completion Date"
                type="date"
                name="estimated_completion_date"
                value={capitalFormValues.estimated_completion_date}
                onChange={handleInputChange}
                errorMessage={capitalFormErrors.estimated_completion_date}
                disabled={!formEnabled}
              />
            </div>
          </div>

          {/* Success/Error messages and buttons */}
          <div
            className={`flex flex-row justify-end items-center gap-6 max-[530px]:flex-col`}
          >
            <SecondaryButton
              onClick={onClose}
              type="button"
              className="!w-48 !h-9"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              className="!w-48 !h-9 disabled:!bg-neutral-300"
            >
              Save
            </PrimaryButton>
          </div>
          <div className="my-4">
            {successMsg && (
              <p className="text-success font-medium !text-sm min-h-[20px] font-roboto">
                {successMsg}
              </p>
            )}
            {errorMsg && (
              <p className="text-error font-medium !text-sm min-h-[20px] font-roboto">
                {errorMsg}
              </p>
            )}
          </div>
        </form>

        {/* Right side */}
        <div className={`flex flex-col gap-6 ${editClasses.goal_container}`}>
          {/* Goal Details */}
          <div
            className={`${editClasses.goal_box} p-4 flex flex-col gap-4 border border-gray-200 rounded-3xl shadow-sm shadow-gray-200`}
          >
            <h2
              className={`${editClasses.goal_box_title} font-semibold text-4xl text-left`}
            >
              ${campaignData.donation_goal} Goal
            </h2>
            <progress
              className={`${editClasses.progress_bar} w-full h-2 my-2`}
              value={campaignData.donation_amount}
              max={campaignData.donation_goal}
            />
            <div className="flex flex-row items-stretch justify-between">
              <div className="flex flex-col">
                <p className="text-gray-500 text-xs font-medium text-left">
                  Goal
                </p>
                <p className="gray-700 font-semibold text-lg">
                  ${campaignData.donation_goal}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs font-medium text-left">
                  Remaining
                </p>
                <p className="gray-700 font-semibold text-lg">
                  $
                  {campaignData.donation_goal - campaignData.donation_amount < 0
                    ? 0
                    : campaignData.donation_goal - campaignData.donation_amount}
                </p>
              </div>
            </div>
          </div>

          {/* QR Code */}
          <CampaignShareBox campaignData={campaignData} />
        </div>
      </div>
    </>
  );
};

export default EditCapitalCampaignForm;
